import { BaseFilePickerInput } from 'components/BaseFilePicker/BaseFilePickerInput';
import { getBaseFilePickerDefaultMessage } from 'components/BaseFilePicker/baseFilePickerMappings';
import { useBaseFilePickerInputStateAndHandlers } from 'components/BaseFilePicker/useBaseFilePickerInputStateAndHandlers';
import { Button } from 'components/Button/Button';
import { FieldError } from 'components/Form/Field/FieldError';
import { Icon } from 'components/Icon/Icon';
import { LoadingDots } from 'components/LoadingDots/LoadingDots';
import { Text } from 'components/Text/Text';
import type { FileUploadStateAndActions } from 'hooks/useFileUpload';
import { useI18n } from 'i18n/hooks/useI18n';
import { colors, fontSizesByType } from 'theme/theme';

import { LargeFilePickerFilename } from './Filename/LargeFilePickerFilename';
import {
  LargeFilePickerContentNoFileSelected,
  LargeFilePickerContentWithFile,
  LargeFilePickerContentWrapper,
  LargeFilePickerWrapper,
} from './LargeFilePicker.styled';

type Props = {
  name: string;
  required: boolean;
  ariaLabel: string | null;
  accept: string | null;
  showDate: boolean;
  disabled: boolean;
  qaId: string;
  downloadAttachment: boolean;
  header: string | null;
  customMessage: string | null;
  fileUploadStateAndActions: FileUploadStateAndActions;
};

export function LargeFilePicker({
  name,
  required,
  ariaLabel,
  accept,
  showDate,
  disabled,
  qaId,
  downloadAttachment,
  header,
  customMessage,
  fileUploadStateAndActions,
}: Props) {
  const { i18n } = useI18n();

  const { fileId, fileMeta, loading, onFileSelected, onFileRemoved, error } =
    fileUploadStateAndActions;

  const { baseFilePickerInputStateAndHandlers } =
    useBaseFilePickerInputStateAndHandlers({ onFileSelected });

  return (
    <LargeFilePickerWrapper data-qa-id={qaId}>
      <BaseFilePickerInput
        name={name}
        qaId={qaId}
        required={required}
        ariaLabel={ariaLabel}
        accept={accept}
        disabled={disabled}
        stateAndHandlers={baseFilePickerInputStateAndHandlers}
      />

      <LargeFilePickerContentWrapper
        data-loading={loading ? true : undefined}
        data-mouse-active={
          baseFilePickerInputStateAndHandlers.mouseActive ? true : undefined
        }
      >
        {(() => {
          if (loading) return <LoadingDots size="default" color="brandBlue" />;

          if (fileId) {
            return (
              <LargeFilePickerContentWithFile>
                <Icon name="paperclip" size={32} colorName="brandBlue" />

                <LargeFilePickerFilename
                  fileId={fileId}
                  filename={fileMeta?.filename ?? null}
                  qaId={qaId}
                  onRemove={onFileRemoved}
                  created={fileMeta && showDate ? fileMeta.created : null}
                  downloadAttachment={downloadAttachment}
                  disabled={disabled}
                />
              </LargeFilePickerContentWithFile>
            );
          }

          return (
            <LargeFilePickerContentNoFileSelected>
              {header && (
                <Text
                  tag="span"
                  color={colors.mediumContentGrey}
                  fontSize={fontSizesByType.h3}
                >
                  {header}
                </Text>
              )}

              <Button
                type="inert"
                icon={{ type: 'normalized', name: 'paperclip' }}
                iconPosition="left"
                variant="primary"
              >
                {customMessage ?? getBaseFilePickerDefaultMessage(i18n)}
              </Button>
            </LargeFilePickerContentNoFileSelected>
          );
        })()}
      </LargeFilePickerContentWrapper>

      {error && <FieldError errorMessage={error} />}
    </LargeFilePickerWrapper>
  );
}
